import { parseISO, format } from 'date-fns';
import { jsPDF } from 'jspdf';
import autoTable from 'jspdf-autotable';
import type { Timecard, TimecardDay } from '../types';
import { formatShiftRange } from './earlyLateReport';
import { resolveScheduleName, type TimecardAnalysisLookupMaps } from './timecardAnalysisReport';
import { formatCompanyDate, formatCompanyDateTime } from './companyDateFormat';

function formatTime(iso: string | null | undefined): string {
  if (!iso) return '—';
  try {
    const d = parseISO(iso);
    if (Number.isNaN(d.getTime())) return '—';
    return format(d, 'HH:mm');
  } catch {
    return '—';
  }
}

function compareYmd(a: string, b: string): number {
  return a.localeCompare(b);
}

/** "HH:mm / HH:mm" from actual in/out (same clock basis as Early/Late). */
export function formatClockInOut(day: TimecardDay): string {
  const a = formatTime(day.actualIn);
  const b = formatTime(day.actualOut);
  return `${a} / ${b}`;
}

export interface BreakReportRow {
  date: string;
  dateDisplay: string;
  firstName: string;
  lastName: string;
  location: string;
  schedule: string;
  shiftDisplay: string;
  clockInOut: string;
  /** Derived from actualBreakMinutes > 0 */
  breakTaken: boolean;
  totalBreakMins: number;
}

export interface BreakReportResult {
  headers: string[];
  rows: string[][];
  dataRows: BreakReportRow[];
  recordsProcessed: number;
}

export type BreakTakenFilter = '' | 'yes' | 'no';

function passesBreakTakenFilter(day: TimecardDay, filter: BreakTakenFilter): boolean {
  const mins = day.actualBreakMinutes ?? 0;
  const taken = mins > 0;
  if (filter === 'yes') return taken;
  if (filter === 'no') return !taken;
  return true;
}

/**
 * Approved timecards only; one row per day in [fromDate, toDate].
 * @param breakTakenFilter '' = all rows; 'yes' = only days with actual break minutes &gt; 0; 'no' = only zero/missing break minutes.
 */
export function buildBreakReport(
  timecards: Timecard[],
  locationMap: Record<number, string>,
  fromDate: string,
  toDate: string,
  breakTakenFilter: BreakTakenFilter = '',
  lookupMaps?: TimecardAnalysisLookupMaps
): BreakReportResult {
  const out: BreakReportRow[] = [];

  for (const tc of timecards || []) {
    const emp = tc.employee;
    const firstName = emp?.firstName ?? '';
    const lastName = emp?.lastName ?? '';
    const locName =
      tc.locationId != null ? locationMap[tc.locationId] ?? `Location #${tc.locationId}` : '—';

    for (const day of tc.days || []) {
      if (!day.date) continue;
      if (compareYmd(day.date, fromDate) < 0 || compareYmd(day.date, toDate) > 0) continue;
      if (!passesBreakTakenFilter(day, breakTakenFilter)) continue;

      const mins = day.actualBreakMinutes ?? 0;
      const taken = mins > 0;

      out.push({
        date: day.date,
        dateDisplay: formatCompanyDate(day.date) || day.date,
        firstName,
        lastName,
        location: locName,
        schedule: resolveScheduleName(day, lookupMaps) || '—',
        shiftDisplay: formatShiftRange(day.scheduledIn, day.scheduledOut),
        clockInOut: formatClockInOut(day),
        breakTaken: taken,
        totalBreakMins: mins,
      });
    }
  }

  out.sort((a, b) => {
    const c = compareYmd(a.date, b.date);
    if (c !== 0) return c;
    return `${a.lastName} ${a.firstName}`.localeCompare(`${b.lastName} ${b.firstName}`);
  });

  const headers = [
    'Date',
    'First name',
    'Last name',
    'Location',
    'Schedule',
    'Shift',
    'Clock In/Out',
    'Break taken',
    'Total Mins.',
  ];

  const rows = breakDataRowsToStringRows(out);

  return {
    headers,
    rows,
    dataRows: out,
    recordsProcessed: out.length,
  };
}

export function breakDataRowsToStringRows(dataRows: BreakReportRow[]): string[][] {
  return dataRows.map((r) => [
    r.dateDisplay,
    r.firstName,
    r.lastName,
    r.location,
    r.schedule,
    r.shiftDisplay,
    r.clockInOut,
    r.breakTaken ? 'Yes' : 'No',
    String(r.totalBreakMins),
  ]);
}

export function createBreakPdfBlob(
  headers: string[],
  rows: string[][],
  meta: {
    fromDate: string;
    toDate: string;
    recordsProcessed: number;
    generatedAt: Date;
    breakTakenFilter: BreakTakenFilter;
  }
): Blob {
  const doc = new jsPDF({ orientation: 'landscape', unit: 'mm', format: 'a4' });
  doc.setFontSize(11);
  doc.text('Break Report', 14, 12);
  doc.setFontSize(9);
  const filterLabel =
    meta.breakTakenFilter === 'yes'
      ? 'Break taken: Yes only'
      : meta.breakTakenFilter === 'no'
        ? 'Break taken: No only'
        : 'Break taken: All';
  doc.text(
    `Run: ${formatCompanyDateTime(meta.generatedAt)}  |  From ${formatCompanyDate(meta.fromDate)} to ${formatCompanyDate(meta.toDate)}  |  Records: ${meta.recordsProcessed}  |  ${filterLabel}`,
    14,
    18
  );
  autoTable(doc, {
    head: [headers],
    body: rows,
    startY: 24,
    styles: { fontSize: 7 },
    headStyles: { fillColor: [60, 60, 60] },
    margin: { left: 14, right: 14 },
  });
  return doc.output('blob');
}
